<template>
    <div class="page__content kb-topic" v-if="topic">
        <div class="columns">
            <div class="column is-9">
                <kb-back :to="{name: 'knowledge-base', params: {lang}}">
                    {{ $t('knowledgebase.backToOverview') }}
                </kb-back>

                <kb-inline-title
                    :value="topic.title || ''"
                    :placeholder="$t('knowledgebase.untitledTopic')"
                    :has-draft="topic.has_draft"
                    :can-revert="topic.has_draft && topic.title_published != null"
                    :disabled="topic.will_be_deleted"
                    @save="saveTitle"
                    @revert="revertTitle"
                />
                <p class="kb-topic__meta">
                    <template v-if="articles.length === 0">{{ $t('knowledgebase.noArticles') }}</template>
                    <template v-else>{{ $tc('knowledgebase.articleCount', articles.length, {count: articles.length}) }}</template>
                    · {{ $t('knowledgebase.updatedPrefix') }} <kb-time :value="topic.updated_at" />
                </p>
            </div>
            <div class="column is-3 has-text-right">
                <button
                    v-if="topic.will_be_deleted"
                    class="button is-warning is-outlined"
                    @click="cancelDeletion"
                >
                    <span class="icon"><i class="fa fa-undo" /></span>
                    <span>{{ $t('knowledgebase.cancelDeletion') }}</span>
                </button>
                <button v-else class="button is-danger is-outlined" @click="confirmDelete">
                    <span class="icon"><i class="fa fa-trash" /></span>
                    <span>
                        {{ topic.title_published != null
                            ? $t('knowledgebase.stageDeletion')
                            : $t('knowledgebase.deleteTopic') }}
                    </span>
                </button>
            </div>
        </div>

        <div
            v-if="topic.will_be_deleted"
            class="notification is-warning kb-topic__deletion-banner"
        >
            {{ $t('knowledgebase.topicPendingDeletion') }}
        </div>

        <section class="kb-topic__articles">
            <div
                class="box kb-article-row"
                :class="{'kb-article-row--pending-deletion': article.will_be_deleted}"
                v-for="article in articles"
                :key="article.id"
                @click="openArticle(article)"
            >
                <div class="kb-article-row__title">
                    <kb-dot
                        v-if="article.has_changes"
                        :title="$t('knowledgebase.unpublishedChanges')"
                    />
                    {{ article.title || $t('knowledgebase.untitledArticle') }}
                </div>
                <div class="kb-article-row__meta">
                    <template v-if="seasonalVariantCount(article) > 0">
                        {{ $tc('knowledgebase.seasonCount', seasonalVariantCount(article), {count: seasonalVariantCount(article)}) }}
                        ·
                    </template>
                    {{ $t('knowledgebase.updatedPrefix') }} <kb-time :value="article.updated_at" />
                </div>
            </div>
            <button class="button is-text kb-topic__add" @click="addArticle">
                + {{ $t('knowledgebase.addArticle') }}
            </button>
        </section>

        <kb-prompt-modal ref="prompt" />
    </div>
</template>

<script>
import { Api } from '../../../api';
import { AppModel } from '../../../models';
import { EventBus } from '../../../utils';
import KbBack from './kb-back.vue';
import KbDot from './kb-dot.vue';
import KbInlineTitle from './kb-inline-title.vue';
import KbPromptModal from './kb-prompt-modal.vue';
import KbTime from './kb-time.vue';

export default {
    name: 'kb-topic',

    components: {KbBack, KbDot, KbInlineTitle, KbPromptModal, KbTime},

    props: ['lang'],

    data() {
        return {
            topic: null,
            articles: [],
        };
    },

    computed: {
        topicId() {
            return Number(this.$route.params.topicId);
        },
    },

    methods: {
        async load() {
            const [topicsRes, articlesRes] = await Promise.all([
                Api.knowledgebase.fetchTopics(),
                Api.knowledgebase.fetchArticles(this.topicId),
            ]);
            const topics = this._results(topicsRes);
            this.topic = topics.find(t => t.id === this.topicId) || null;
            this.articles = this._results(articlesRes);
        },
        _results(res) {
            const body = res.body;
            return body && body.results ? body.results : (body || []);
        },
        async saveTitle(next) {
            await Api.knowledgebase.updateTopic(this.topic.id, {title_draft: next});
            await this.load();
        },
        async revertTitle() {
            await Api.knowledgebase.discardTopicDraft(this.topic.id);
            await this.load();
        },
        async addArticle() {
            const title = await this.$refs.prompt.prompt({
                title: this.$t('knowledgebase.addArticle'),
                placeholder: this.$t('knowledgebase.promptArticleTitle'),
                submitText: this.$t('knowledgebase.create'),
            });
            if(title == null) return;
            const res = await Api.knowledgebase.createArticle({
                topic: this.topicId,
                title_draft: title,
            });
            this.openArticle(res.body);
        },
        openArticle(article) {
            this.$router.push({
                name: 'kb-article-editor',
                params: {lang: this.lang, articleId: article.id},
            });
        },
        seasonalVariantCount(article) {
            return (article.variants || []).filter(
                v => v.season != null && v.valid_from == null
            ).length;
        },
        _onBootstrap() {
            if(AppModel.activeCampground) this.load();
        },
        async confirmDelete() {
            const isLive = this.topic.title_published != null;
            const message = isLive
                ? this.$t('knowledgebase.confirmDeleteTopicLive')
                : this.$t('knowledgebase.confirmDeleteTopicDraftOnly');

            const ok = await this.$refs.prompt.confirm({
                title: this.$t('knowledgebase.deleteTopic'),
                message,
                submitText: isLive
                    ? this.$t('knowledgebase.stageDeletion')
                    : this.$t('knowledgebase.delete'),
                danger: true,
            });
            if(!ok) return;
            await Api.knowledgebase.deleteTopic(this.topic.id);
            if(isLive) {
                // Topic still exists, just staged. Reload to show strikethrough.
                await this.load();
            } else {
                this.$router.push({name: 'knowledge-base', params: {lang: this.lang}});
            }
        },
        async cancelDeletion() {
            await Api.knowledgebase.restoreTopic(this.topic.id);
            await this.load();
        },
    },

    created() {
        if(AppModel.activeCampground) {
            this.load();
        }
        EventBus.$on(EventBus.BOOTSTRAP_DONE, this._onBootstrap);
    },

    beforeDestroy() {
        EventBus.$off(EventBus.BOOTSTRAP_DONE, this._onBootstrap);
    },

    watch: {
        '$route.params.topicId'() {
            if(AppModel.activeCampground) this.load();
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../../variables';

.kb-topic__deletion-banner {
    margin-bottom: 16px;
}

.kb-topic__meta {
    font-size: 0.9rem;
    color: #666;
    margin-top: 6px;
}

.kb-topic__add {
    margin-top: 8px;
}

.kb-article-row {
    cursor: pointer;
    transition: box-shadow 0.15s;
    margin-bottom: 8px;

    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    &__title {
        font-weight: 500;
    }

    &__meta {
        font-size: 0.85rem;
        color: #888;
        margin-top: 4px;
    }

    &--pending-deletion {
        @include kb-pending-deletion('.kb-article-row__title');
    }
}
</style>



// WEBPACK FOOTER //
// src/views/pages/knowledge-base/kb-topic.vue